<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-sm-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h4 class="card-title mb-0">{{ pageTitle }}</h4>
          </div>
          <div class="card-body">
            <!-- 올리기 — 여러 파일 한꺼번에. BL 은 파일 이름에서(본문 BL 로 교차확인), 서류 종류는 쪽 내용으로 -->
            <div class="border rounded p-2 mb-3 bg-light" @dragover.prevent @drop.prevent="onDrop">
              <div class="d-flex align-items-center flex-wrap cst__gap">
                <span class="font-weight-bold">⬆ 上传请款单/结算单(청구서·정산서 올리기)</span>
                <label class="btn btn-sm btn-primary mb-0" :class="{ disabled: uploading }">
                  选择PDF(PDF 선택 — 여러 개 가능)
                  <input ref="files" type="file" accept="application/pdf,.pdf" multiple class="d-none" :disabled="uploading" @change="onPick">
                </label>
                <b-spinner v-if="uploading" small></b-spinner>
                <span v-if="uploading" class="small text-info">{{ uploadingText }}</span>
                <!-- 관세사 메일 자동 수집(15분마다) — 지금 바로 한 번 -->
                <b-button size="sm" variant="outline-info" class="ml-auto" :disabled="mailSyncing" @click="mailSync">📧 立即收取邮件(메일 지금 확인)</b-button>
                <b-spinner v-if="mailSyncing" small></b-spinner>
                <span v-if="mailState" class="small" :class="mailState.ok ? 'text-muted' : 'text-danger'">
                  邮件 {{ dt(mailState.lastRunAt) }} · {{ mailState.ok ? mailCountsText(mailState.counts) : ('失败(실패) ' + (mailState.error || '')) }}
                </span>
              </div>
              <small class="text-muted d-block mt-1">
                文件名里要有BL号(파일 이름에 BL 번호가 있어야 합니다 — 예: 「GSPC26064E803A3 쿠패스 청구서.pdf」). 结算单会自动分成「结算单」和「进口申报证」两个文件保存，原件也保存(정산서는 「정산서」·「수입신고필증」 두 파일로 나눠 저장하고 원본도 남깁니다). 同一BL再上传 = 替换，旧文件保留在记录里(같은 BL 을 다시 올리면 교체, 옛 파일은 이력에 남습니다).
              </small>
              <div v-if="results.length" class="mt-2">
                <table class="table table-sm table-bordered mb-0 small bg-white">
                  <thead class="thead-light"><tr><th>文件(파일)</th><th>BL</th><th>种类(종류)</th><th>页数(쪽)</th><th>核对(검산)</th><th>提示(경고)</th></tr></thead>
                  <tbody>
                    <tr v-for="(r, i) in results" :key="'res' + i" :class="{ 'table-danger': r.error, 'table-warning': !r.error && r.warnings && r.warnings.length }">
                      <td>{{ r.name }}</td>
                      <td>{{ r.blNo || '' }}</td>
                      <td>{{ r.kinds ? r.kinds.map(kindLabel).join(' + ') : '' }}</td>
                      <td>{{ r.pagesText || '' }}</td>
                      <td>{{ r.checkText || '' }}</td>
                      <td>
                        <span v-if="r.error" class="text-danger">{{ r.error }}</span>
                        <div v-if="r.unassigned" class="text-danger">⚠ 未分类 — 请在下面指定BL(미분류 — 아래에서 BL 을 지정해 주세요)</div>
                        <div v-if="r.duplicate" class="text-muted">同一文件已存在(같은 파일이 이미 있음 — 저장 안 함)</div>
                        <div v-for="(w, j) in (r.warnings || [])" :key="'w' + j">⚠ {{ w }}</div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- 미분류 — BL·종류를 못 정한 파일. BL(필요하면 종류도) 을 지정하면 그 BL 의 서류가 된다 -->
            <div v-if="unassigned.length" class="border border-danger rounded p-2 mb-3">
              <div class="font-weight-bold text-danger mb-1">❓ 未分类文件(미분류 파일) {{ unassigned.length }}</div>
              <table class="table table-sm table-bordered mb-0 small">
                <thead class="thead-light"><tr><th>文件(파일)</th><th>收到(받은 때)</th><th>文件内BL(서류 안 BL)</th><th>提示(경고)</th><th style="min-width:330px">指定(지정)</th></tr></thead>
                <tbody>
                  <tr v-for="u in unassigned" :key="'un' + u._id">
                    <td><a :href="fileUrl(u.files.original)" target="_blank">📄 {{ u.fileName }}</a> <span class="text-muted">({{ u.pageCount }}쪽)</span><div v-if="u.source && u.source.type === 'mail'" class="text-muted">📧 {{ u.source.subject }}</div></td>
                    <td>{{ dt(u.uploadedAt) }}<div class="text-muted">{{ u.uploadedBy }}</div></td>
                    <td>{{ u.bodyBl || u.blNo }}</td>
                    <td><div v-for="(w, j) in u.warnings" :key="'uw' + j">⚠ {{ w }}</div></td>
                    <td>
                      <div class="d-flex cst__gap">
                        <input v-model.trim="assignBl[u._id]" class="form-control form-control-sm" style="width:170px" placeholder="BL">
                        <select v-if="!u.kinds.length" v-model="assignKind[u._id]" class="form-control form-control-sm" style="width:120px">
                          <option value="">种类(종류)</option><option value="claim">请款单(청구서)</option><option value="settlement">结算单(정산서)</option>
                        </select>
                        <b-button size="sm" variant="primary" :disabled="busy" @click="assign(u)">确定(지정)</b-button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- BL 손으로 추가 — 포워더에게 받은 BL 목록 붙여넣기(출고 흐름의 BL 리스트가 시스템에 들어오기 전까지) -->
            <details class="mb-3">
              <summary class="small font-weight-bold">➕ 添加BL(BL 추가 — 포워더 BL 목록 붙여넣기)</summary>
              <div class="d-flex align-items-start flex-wrap cst__gap mt-2">
                <textarea v-model="blText" rows="4" class="form-control form-control-sm" style="width:420px" placeholder="GSPC26064E801A1 COUPASS 综合&#10;GSPC26064E802A4 FLODIT 单独"></textarea>
                <div>
                  <b-button size="sm" variant="outline-primary" :disabled="!blText.trim() || busy" @click="addBl">添加(추가)</b-button>
                  <small class="text-muted d-block mt-1">一行一个BL，后面的文字作为备注(한 줄에 BL 하나, 뒤의 글자는 메모로 남습니다).</small>
                </div>
              </div>
            </details>

            <!-- 出库装船BL清单(BL 리스트) — 箱单发票列表 기록에서 자동 생성. 关税结算单 값(아래 표)을 BL 첫 줄에 옮긴다. 엑셀 = 샘플 열 순서 그대로 (2026-10-12 YZY) -->
            <div class="border rounded p-2 mb-3">
              <div class="d-flex align-items-center flex-wrap cst__gap">
                <span class="font-weight-bold">📋 出库装船BL清单(출고 선적 BL 리스트)</span>
                <label class="mb-0 small">出库日(출고일)</label>
                <select v-model="sheetDate" class="form-control form-control-sm" style="width:150px" @change="loadSheet">
                  <option v-for="d in sheetDates" :key="d" :value="d">{{ d }}</option>
                </select>
                <b-button size="sm" variant="outline-success" :disabled="!sheetRows.length" @click="downloadSheet">⬇ Excel</b-button>
                <b-spinner v-if="sheetLoading" small></b-spinner>
                <span v-if="sheetError" class="small text-danger">{{ sheetError }}</span>
              </div>
              <small class="text-muted d-block mt-1">
                箱单发票列表里该出库日的记录自动生成。黄色格子可直接修改，只在屏幕和下载的Excel里生效，不会保存(그날 서류목록 기록에서 자동 생성합니다. 노란 칸은 화면에서 고칠 수 있고 화면·내려받는 엑셀에만 반영되며 저장되지 않습니다 — 분류·정밀검수·배차·其他金额·운송료·환율·성명·이메일). CNY=合计÷汇率(합계÷환율).
              </small>
              <div v-if="sheetRows.length" class="table-responsive mt-2 cst__sheet">
                <table class="table table-sm table-bordered mb-0 small">
                  <thead class="thead-light"><tr><th v-for="(h, i) in sheetHeaders" :key="'sh' + i" class="text-nowrap" style="white-space:pre-line">{{ h }}</th></tr></thead>
                  <tbody>
                    <tr v-for="(r, ri) in sheetRows" :key="'sr' + ri"><td v-for="(c, ci) in r" :key="ci" :class="{ 'cst__edit': sheetEditable.includes(ci) }" style="white-space:pre-line"><textarea v-if="sheetEditable.includes(ci)" class="form-control form-control-sm" style="min-width:90px" :rows="Math.max(1, String(c).split('\n').length)" :value="c" @input="editSheet(ri, ci, $event.target.value)"></textarea><template v-else>{{ c }}</template></td></tr>
                  </tbody>
                </table>
              </div>
              <div v-else-if="!sheetLoading" class="small text-muted mt-2">这一天没有出库记录(이 출고일의 서류목록 기록이 없습니다)</div>
            </div>

            <div class="font-weight-bold mb-1">🧾 关税结算单(관세사 정산서 현황)</div>
            <!-- 단계별 개수 — 빠진 것(청구서 대기)이 먼저 눈에 띄게 -->
            <div class="d-flex flex-wrap cst__gap mb-2">
              <button v-for="t in tabs" :key="t.k" type="button" class="btn btn-sm" :class="tab === t.k ? 'btn-dark' : t.cls" @click="tab = t.k">
                {{ t.label }} <b>{{ count(t.k) }}</b>
              </button>
              <input v-model.trim="search" type="search" class="form-control form-control-sm" style="width:200px" placeholder="BL / 货主(화주)">
              <b-button size="sm" variant="outline-secondary" :disabled="loading" @click="load">刷新(새로고침)</b-button>
              <b-spinner v-if="loading" small></b-spinner>
            </div>
            <div v-if="error" class="text-danger small mb-2">{{ error }}</div>

            <div class="table-responsive cst__table">
              <table class="table table-sm table-bordered mb-0 small">
                <thead class="thead-light">
                  <tr>
                    <th style="min-width:140px">BL</th><th>阶段(단계)</th><th>出库日(출고일)</th><th>货主(화주)</th><th style="min-width:80px">清关(통관)</th><th style="min-width:110px">来源(출처)</th>
                    <th style="min-width:170px">请款单(청구서)</th><th style="min-width:230px">结算单(정산서)</th><th>费用归属(비용 주인)</th><th style="min-width:80px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="r in shown" :key="r.blNo" :class="rowClass(r)">
                    <td>
                      <b>{{ r.blNo }}</b>
                      <div v-if="r.unknownBl" class="text-danger">⚠ 系统里没有的BL(시스템에 없는 BL)</div>
                      <div v-if="r.manual && r.manual.memo" class="text-muted">{{ r.manual.memo }}</div>
                    </td>
                    <td><span class="badge" :class="stageBadge(r.stage)">{{ stageLabel(r.stage) }}</span></td>
                    <td>{{ r.출고일 }}</td>
                    <td>{{ r.companyids.join(', ') }}</td>
                    <td>{{ r.clearance.join(', ') }}</td>
                    <td>{{ r.sources.map(sourceLabel).concat(r.manual ? ['手动(수동)'] : []).join(', ') }}</td>
                    <td>
                      <template v-if="r.claim">
                        <a :href="fileUrl(r.claim.files.original || r.claim.files.claim)" target="_blank">📄 请款单(청구서)</a>
                        <div>请款 {{ won(r.claim.청구금액 != null ? r.claim.청구금액 : r.claim.비용합계) }}</div>
                        <div class="text-muted">{{ day(r.claim.uploadedAt) }}</div>
                        <div v-if="r.claim.warnings.length" class="text-warning">⚠ {{ r.claim.warnings.length }}</div>
                      </template>
                      <span v-else class="text-danger">未上传(없음)</span>
                    </td>
                    <td>
                      <template v-if="r.settlement">
                        <div>
                          <a v-if="r.settlement.files.settlement" :href="fileUrl(r.settlement.files.settlement)" target="_blank">📄 结算单(정산서 {{ r.settlement.files.settlement.pages }}쪽)</a>
                        </div>
                        <div>
                          <a v-if="r.settlement.files.declaration" :href="fileUrl(r.settlement.files.declaration)" target="_blank">📄 进口申报证(수입신고필증 {{ r.settlement.files.declaration.pages }}쪽)</a>
                        </div>
                        <div><a :href="fileUrl(r.settlement.files.original)" target="_blank" class="text-muted">原件(원본 {{ r.settlement.pageCount }}쪽)</a></div>
                        <div>税 {{ won(r.settlement.총세액) }} · 合计 {{ won(r.settlement.비용합계) }}<span v-if="r.settlement.입금일자"> · 付款 {{ r.settlement.입금일자 }}</span></div>
                        <div v-if="r.settlement.신고번호" class="text-muted">{{ r.settlement.신고번호 }} · {{ r.settlement.란수 }}란</div>
                        <div :class="r.settlement.checksOk ? 'text-success' : 'text-danger'">核对(검산) {{ r.settlement.checksOk ? '✓' : '✗' }} {{ r.settlement.checkCount }}</div>
                        <div v-for="(w, j) in r.settlement.warnings" :key="'sw' + j" class="text-warning">⚠ {{ w }}</div>
                      </template>
                      <span v-else :class="r.stage === 'claim' ? 'text-danger' : 'text-muted'">未上传(없음)</span>
                    </td>
                    <td>
                      {{ ownerLabel(r.costOwner) }}
                      <!-- 화주단독 — 화주 구매요청 매입가로 나눠 담은 결과(화주통관비) -->
                      <template v-if="r.costOwner === 'shipper'">
                        <div v-if="r.shipperAlloc" :class="allocClass(r.shipperAlloc)">{{ allocText(r.shipperAlloc) }}</div>
                        <div v-for="(w, j) in (r.shipperAlloc && r.shipperAlloc.warnings) || []" :key="'aw' + j" class="text-warning">⚠ {{ w }}</div>
                        <b-button v-if="r.settlement" size="sm" variant="outline-info" class="mt-1" :disabled="busy" @click="reallocate(r.blNo)">重新分配(다시 배분)</b-button>
                      </template>
                    </td>
                    <td class="text-nowrap">
                      <label class="btn btn-sm btn-outline-primary mb-1" :class="{ disabled: uploading }">
                        上传(올리기)
                        <input type="file" accept="application/pdf,.pdf" multiple class="d-none" :disabled="uploading" @change="onPickFor(r.blNo, $event)">
                      </label>
                      <b-button size="sm" variant="outline-secondary" class="mb-1" @click="openHistory(r.blNo)">记录(이력)</b-button>
                      <b-button v-if="r.manual && !r.claim && !r.settlement" size="sm" variant="outline-danger" class="mb-1" @click="removeBl(r.blNo)">删除(빼기)</b-button>
                    </td>
                  </tr>
                  <tr v-if="!shown.length"><td colspan="10" class="text-center text-muted">没有(없음)</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-modal v-model="historyOpen" :title="'记录(이력) ' + historyBl" size="xl" hide-footer>
      <b-spinner v-if="historyLoading" small></b-spinner>
      <div v-for="u in historyList" :key="u._id" class="border rounded p-2 mb-2" :class="{ 'cst__replaced': u.replacedBy }">
        <div class="d-flex flex-wrap cst__gap align-items-center">
          <b>{{ u.kinds.map(kindLabel).join(' + ') }}</b>
          <span>{{ u.fileName }}</span>
          <span class="text-muted">{{ dt(u.uploadedAt) }} · {{ u.uploadedBy }}</span>
          <span v-if="u.replacedBy" class="badge badge-secondary">已替换(교체됨)</span>
          <span v-else class="badge badge-success">当前(현재)</span>
        </div>
        <div class="mt-1">
          <a v-for="p in fileParts(u)" :key="p.k" :href="fileUrl(u.files[p.k])" target="_blank" class="mr-3">📄 {{ p.label }} ({{ u.files[p.k].pages }}쪽)</a>
        </div>
        <div class="small mt-1">
          <span v-for="(c, j) in u.checks" :key="'c' + j" class="mr-3" :class="c.ok ? 'text-success' : 'text-danger'">{{ c.ok ? '✓' : '✗' }} {{ c.name }} <span class="text-muted">({{ c.detail }})</span></span>
        </div>
        <div v-for="(w, j) in u.warnings" :key="'hw' + j" class="small text-warning">⚠ {{ w }}</div>
        <details v-if="u.parsed && u.parsed.declaration" class="small mt-1">
          <summary>进口申报证 品目(신고필증 란 {{ u.parsed.declaration.란.length }}개)</summary>
          <table class="table table-sm table-bordered mb-0">
            <thead><tr><th>란</th><th>品名(품명)</th><th>거래품명</th><th>HS</th><th>과세가격 ₩</th><th>관세</th><th>부가세</th></tr></thead>
            <tbody>
              <tr v-for="l in u.parsed.declaration.란" :key="'l' + l.란번호">
                <td>{{ l.란번호 }}</td><td>{{ l.품명 }}</td><td>{{ l.거래품명 }}</td><td>{{ l.세번부호 }}</td><td class="text-right">{{ won(l.과세가격KRW) }}</td><td class="text-right">{{ won(l.관세) }}</td><td class="text-right">{{ won(l.부가세) }}</td>
              </tr>
            </tbody>
          </table>
        </details>
      </div>
      <div v-if="!historyLoading && !historyList.length" class="text-muted">没有记录(기록 없음)</div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';
import { coupassMenuTitle } from './coupassMenu';
import { writeSheetXlsx, blSheetFileName, sheetRowsForXlsx, cnyFromRow } from './blSheetXlsx';

const STAGE_LABEL = { none: '待请款单(청구서 대기)', claim: '已请款·待付款(청구서 있음·입금 대기)', settlement: '已结算(정산서 있음)' };
const KIND_LABEL = { claim: '请款单(청구서)', settlement: '结算单(정산서)' };
const SOURCE_LABEL = { shipment: '出库清单(출고리스트)', reqpurchase: '已出库(출고정보)', invoice: '箱单发票(서류목록)' };

function readBase64(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result).replace(/^data:[^,]*,/, ''));
    fr.onerror = () => reject(fr.error || new Error('파일을 읽지 못했습니다'));
    fr.readAsDataURL(file);
  });
}

// 关税结算单 — 관세사가 BL 마다 보내는 청구서·정산서를 빠짐없이 모은다. 저장은 /api/coupassCustoms/*(inventory-services/customs-settlement-routes.js)
export default {
  name: 'CustomsSettlementList',
  data() {
    return {
      pageTitle: coupassMenuTitle('logis.customssettlement'),
      rows: [],
      unassigned: [],
      mailState: null,
      mailSyncing: false,
      assignBl: {},
      assignKind: {},
      loading: false,
      busy: false,
      error: '',
      tab: 'missing',
      sheetDate: '',
      sheetDates: [],
      sheetHeaders: [],
      sheetRows: [],
      sheetLoading: false,
      sheetError: '',
      sheetEditable: [],
      sheetCalc: null,
      search: '',
      uploading: false,
      uploadingText: '',
      results: [],
      blText: '',
      historyOpen: false,
      historyBl: '',
      historyList: [],
      historyLoading: false,
      tabs: [
        { k: 'all', label: '全部(전체)', cls: 'btn-outline-secondary' },
        { k: 'missing', label: '未结算(정산서 없음)', cls: 'btn-outline-danger' },
        { k: 'none', label: '待请款单(청구서 대기)', cls: 'btn-outline-danger' },
        { k: 'claim', label: '待付款(입금 대기)', cls: 'btn-outline-warning' },
        { k: 'settlement', label: '已结算(정산서 있음)', cls: 'btn-outline-success' },
        { k: 'unknown', label: '系统外BL(시스템에 없는 BL)', cls: 'btn-outline-danger' },
        { k: 'warn', label: '有提示(경고)', cls: 'btn-outline-warning' }
      ]
    };
  },
  computed: {
    searched() {
      const q = this.search.toUpperCase();
      if (!q) return this.rows;
      return this.rows.filter(r => r.blNo.includes(q) || r.companyids.some(c => String(c).toUpperCase().includes(q)) || (r.manual && String(r.manual.memo || '').toUpperCase().includes(q)));
    },
    shown() { return this.searched.filter(r => this.inTab(r, this.tab)); }
  },
  mounted() {
    document.title = this.pageTitle;
    this.load();
    this.loadMailState();
    this.loadSheet();
  },
  methods: {
    // 出库装船BL清单 — 날짜를 안 고르면 서버가 오늘(한국 날짜)을 준다. 그날 기록이 없으면 가장 최근 출고일로 연다
    async loadSheet(ev) {
      this.sheetLoading = true;
      this.sheetError = '';
      try {
        const asked = ev ? this.sheetDate : '';
        let { data } = await axios.post('/api/coupassCustoms/blSheet', { date: asked || undefined });
        if (data.요청결과 !== '성공') throw new Error((data.error && data.error.errorMessage) || '失败(실패)');
        if (!asked && !data.rows.length && data.dates.length && data.dates[0] !== data.date) {
          ({ data } = await axios.post('/api/coupassCustoms/blSheet', { date: data.dates[0] }));
          if (data.요청결과 !== '성공') throw new Error((data.error && data.error.errorMessage) || '失败(실패)');
        }
        this.sheetDate = data.date;
        this.sheetDates = data.dates.includes(data.date) ? data.dates : [data.date].concat(data.dates);
        this.sheetHeaders = data.headers;
        this.sheetRows = data.rows;
        this.sheetEditable = data.editable || [];
        this.sheetCalc = data.calc || null;
      } catch (e) {
        console.error('BL清单 불러오기 실패', e);
        this.sheetError = e.message;
      } finally {
        this.sheetLoading = false;
      }
    },
    // 화면에서 칸을 고친다 — 서버에 저장하지 않는다. 汇率 를 고치면 CNY 금액도 다시 계산
    editSheet(ri, ci, v) {
      const row = this.sheetRows[ri];
      this.$set(row, ci, v);
      if (this.sheetCalc && ci === this.sheetCalc.rate) this.$set(row, this.sheetCalc.cny, cnyFromRow(row, this.sheetCalc));
    },
    downloadSheet() {
      writeSheetXlsx(this.sheetHeaders, sheetRowsForXlsx(this.sheetRows, this.sheetEditable), 'Sheet1', blSheetFileName(this.sheetDate));
    },
    inTab(r, k) {
      if (k === 'all') return true;
      if (k === 'missing') return r.stage !== 'settlement';
      if (k === 'unknown') return r.unknownBl;
      if (k === 'warn') return [r.claim, r.settlement].some(u => u && (u.warnings.length || !u.checksOk));
      return r.stage === k;
    },
    count(k) { return this.searched.filter(r => this.inTab(r, k)).length; },
    stageLabel(s) { return STAGE_LABEL[s] || s; },
    stageBadge(s) { return s === 'settlement' ? 'badge-success' : s === 'claim' ? 'badge-warning' : 'badge-danger'; },
    kindLabel(k) { return KIND_LABEL[k] || k; },
    sourceLabel(s) { return SOURCE_LABEL[s] || s; },
    ownerLabel(o) { return o === 'coupass' ? 'COUPASS自付(쿠패스 자체 비용)' : o === 'shipper' ? '货主费用(화주 비용)' : ''; },
    // 화주통관비 배분 상태 — 「已分配 n件 / 未匹配 m栏(배분됨 n건 / 미매칭 m란)」
    allocText(a) {
      if (a.status === 'allocated') return `已分配 ${a.배분건수}件 / 未匹配 ${a.미매칭란수}栏(배분됨 ${a.배분건수}건 / 미매칭 ${a.미매칭란수}란) ₩${this.won(a.배분합계)}`;
      const L = { noShipment: '没有出库记录·未分配(이 BL 선적 없음 — 배분 안 됨)', coupass: 'COUPASS自付·不分配(쿠패스 비용 — 배분 안 함)', unknownOwner: '进口人不明(수입자 미확인)', noSettlement: '没有结算单(정산서 없음)', multiCompany: '多个货主·未分配(화주 여럿 — 배분 안 됨)', noCost: '费用读取失败(비용 판독 실패)' };
      return L[a.status] || a.status;
    },
    allocClass(a) { return a.status === 'allocated' ? (a.미매칭란수 ? 'text-warning' : 'text-success') : 'text-danger'; },
    async reallocate(blNo) {
      this.busy = true;
      try {
        const { data } = await axios.post('/api/coupassCustoms/allocate', { blNo });
        if (data.요청결과 !== '성공') throw new Error((data.error && data.error.errorMessage) || '배분 실패');
        await this.load();
      } catch (e) {
        console.error('[CustomsSettlementList] reallocate', e);
        alert(e.message);
      } finally {
        this.busy = false;
      }
    },
    rowClass(r) { return r.unknownBl ? 'table-danger' : r.stage === 'none' ? 'cst__missing' : ''; },
    won(v) { return v === null || v === undefined ? '-' : Number(v).toLocaleString(); },
    day(v) { return v ? String(v).slice(0, 10) : ''; },
    dt(v) { return v ? new Date(v).toLocaleString() : ''; },
    fileUrl(f) { return f && f.fileId ? `/api/coupassCustoms/file/${f.fileId}?name=${encodeURIComponent(f.name || 'file.pdf')}` : '#'; },
    fileParts(u) {
      return [{ k: 'claim', label: '请款单(청구서)' }, { k: 'settlement', label: '结算单(정산서)' }, { k: 'declaration', label: '进口申报证(수입신고필증)' }, { k: 'original', label: '原件(원본)' }]
        .filter(p => u.files && u.files[p.k] && u.files[p.k].fileId);
    },
    async load() {
      this.loading = true;
      this.error = '';
      try {
        const { data } = await axios.post('/api/coupassCustoms/list', {});
        if (data.요청결과 !== '성공') throw new Error((data.error && data.error.errorMessage) || '목록 실패');
        this.rows = data.rows || [];
        this.unassigned = data.unassigned || [];
        // 지정 칸 기본값 — 파일 이름·서류 안에서 찾은 BL(종류만 모르는 경우)
        this.unassigned.forEach(u => {
          if (this.assignBl[u._id] === undefined) this.$set(this.assignBl, u._id, u.blNo || u.bodyBl || '');
          if (this.assignKind[u._id] === undefined) this.$set(this.assignKind, u._id, '');
        });
      } catch (e) {
        console.error('[CustomsSettlementList] load', e);
        this.error = (e.response && e.response.data && e.response.data.error && e.response.data.error.errorMessage) || e.message;
      } finally {
        this.loading = false;
      }
    },
    onDrop(ev) { this.uploadFiles([...((ev.dataTransfer && ev.dataTransfer.files) || [])], ''); },
    onPick(ev) { const fs = [...ev.target.files]; ev.target.value = ''; this.uploadFiles(fs, ''); },
    onPickFor(blNo, ev) { const fs = [...ev.target.files]; ev.target.value = ''; this.uploadFiles(fs, blNo); },
    mailCountsText(c) {
      const L = { saved: '保存(저장)', duplicate: '重复(중복)', unassigned: '未分类(미분류)', skip: '跳过(건너뜀)' };
      const parts = Object.keys(c || {}).map(k => `${L[k] || k} ${c[k]}`);
      return parts.length ? parts.join(' · ') : '无新邮件(새 메일 없음)';
    },
    async loadMailState() {
      try {
        const { data } = await axios.post('/api/coupassCustoms/mailState', {});
        if (data.요청결과 === '성공') this.mailState = data.state;
        else console.error('[CustomsSettlementList] mailState', data.error);
      } catch (e) {
        console.error('[CustomsSettlementList] mailState', e);
      }
    },
    async mailSync() {
      this.mailSyncing = true;
      try {
        const { data } = await axios.post('/api/coupassCustoms/mailSync', {});
        if (data.요청결과 !== '성공') throw new Error((data.error && data.error.errorMessage) || '메일 확인 실패');
        await this.load();
      } catch (e) {
        console.error('[CustomsSettlementList] mailSync', e);
        alert(e.message);
      } finally {
        this.mailSyncing = false;
        this.loadMailState();
      }
    },
    async assign(u) {
      const blNo = String(this.assignBl[u._id] || u.bodyBl || '').trim().toUpperCase();
      if (!blNo) { alert('BL 번호를 넣어 주세요(请输入BL号)'); return; }
      this.busy = true;
      try {
        const { data } = await axios.post('/api/coupassCustoms/assign', { uploadId: u._id, blNo, kind: this.assignKind[u._id] || undefined });
        if (data.요청결과 !== '성공') throw new Error((data.error && data.error.errorMessage) || '지정 실패');
        await this.load();
      } catch (e) {
        console.error('[CustomsSettlementList] assign', e);
        alert(e.message);
      } finally {
        this.busy = false;
      }
    },
    // 한 개씩 차례로 — 수십 MB 묶음이 여러 개면 한꺼번에 보내면 서버·브라우저가 버겁다
    async uploadFiles(files, blNo) {
      const pdfs = files.filter(f => /\.pdf$/i.test(f.name) || f.type === 'application/pdf');
      if (!pdfs.length) return;
      this.uploading = true;
      try {
        for (let i = 0; i < pdfs.length; i++) {
          const f = pdfs[i];
          this.uploadingText = `${i + 1}/${pdfs.length} ${f.name}`;
          const res = { name: f.name };
          try {
            const data64 = await readBase64(f);
            const { data } = await axios.post('/api/coupassCustoms/upload', { file: { name: f.name, type: 'application/pdf', data: data64 }, blNo: blNo || undefined });
            if (data.요청결과 !== '성공') throw new Error((data.error && data.error.errorMessage) || '업로드 실패');
            const u = data.upload;
            Object.assign(res, {
              blNo: u.blNo,
              kinds: u.kinds,
              pagesText: [`${u.pageCount}쪽`].concat(this.fileParts(u).filter(p => p.k !== 'original').map(p => `${p.label.split('(')[0]} ${u.files[p.k].pages}`)).join(' · '),
              checkText: u.checkCount ? (u.checksOk ? `✓ ${u.checkCount}` : `✗ ${(data.checks || []).filter(c => !c.ok).length}/${u.checkCount}`) : '-',
              unassigned: !!data.unassigned,
              duplicate: !!data.duplicate,
              warnings: u.warnings.concat(data.replaced ? ['已替换旧文件(옛 파일 교체 — 이력에 남음)'] : [])
            });
          } catch (e) {
            console.error('[CustomsSettlementList] upload', f.name, e);
            res.error = (e.response && e.response.data && e.response.data.error && e.response.data.error.errorMessage) || e.message;
          }
          this.results.unshift(res);
        }
      } finally {
        this.uploading = false;
        this.uploadingText = '';
        this.load();
      }
    },
    async addBl() {
      this.busy = true;
      try {
        const { data } = await axios.post('/api/coupassCustoms/addBl', { text: this.blText });
        if (data.요청결과 !== '성공') throw new Error((data.error && data.error.errorMessage) || '추가 실패');
        this.blText = '';
        this.$bvToast && this.$bvToast.toast(`BL ${data.count}개 추가(新 ${data.inserted})`, { variant: 'success', autoHideDelay: 2500 });
        await this.load();
      } catch (e) {
        console.error('[CustomsSettlementList] addBl', e);
        alert(e.message);
      } finally {
        this.busy = false;
      }
    },
    async removeBl(blNo) {
      if (!confirm(`${blNo} 删除?(빼기)`)) return;
      try {
        const { data } = await axios.post('/api/coupassCustoms/removeBl', { blNo });
        if (data.요청결과 !== '성공') throw new Error((data.error && data.error.errorMessage) || '실패');
        await this.load();
      } catch (e) {
        console.error('[CustomsSettlementList] removeBl', e);
        alert(e.message);
      }
    },
    async openHistory(blNo) {
      this.historyBl = blNo;
      this.historyOpen = true;
      this.historyLoading = true;
      this.historyList = [];
      try {
        const { data } = await axios.post('/api/coupassCustoms/history', { blNo });
        if (data.요청결과 !== '성공') throw new Error((data.error && data.error.errorMessage) || '실패');
        this.historyList = data.uploads || [];
      } catch (e) {
        console.error('[CustomsSettlementList] history', e);
        alert(e.message);
      } finally {
        this.historyLoading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.cst {
  &__gap { gap: 8px; }
  &__table {
    max-height: 72vh;
    overflow-y: auto;
    table { overflow: visible; }
    thead th { position: sticky; top: 0; z-index: 2; }
  }
  &__sheet {
    max-height: 50vh;
    overflow: auto;
    thead th { position: sticky; top: 0; z-index: 2; }
  }
  &__edit { background: #fffbe0; }
  &__missing { background: #fff5f5; }
  &__replaced { opacity: .6; }
}
</style>
